/**
 * Account avatar + menu for the detail page's sticky top bar.
 *
 * The v2 detail route opts out of the shared SiteHeader entirely
 * (`handle = { hideSiteHeader: true }` — the page is full-bleed and owns its
 * own top bar), which left the account menu unreachable from this page. This
 * restores it in the page's OWN bar, beside Accept/Decline, rather than by
 * un-hiding the site header — a second header band would fight the sticky bar
 * for vertical space and duplicate the back button.
 *
 * Reads the same root-loader user and `createAvatarName` initials as
 * SiteHeader so the two read identically, and offers the same two live
 * destinations that header offers today: Settings and Logout. Built on the
 * house DropdownMenu (like KebabMenu next to it) rather than SiteHeader's
 * manual open-state + document mousedown listener, so outside-click, Escape
 * and focus return come from the primitive and no `useEffect` is needed.
 *
 * Logout posts through a PLAIN `<form>`, not `useSubmit`/`<Form>`: this page
 * is rendered inside a bare MemoryRouter by its tests, and every data-router
 * hook throws there ("useSubmit must be used within a data router"). A native
 * POST to /logout hits the same route action and redirects, and it keeps the
 * component renderable outside a data router. The form sits OUTSIDE the menu
 * content so closing the menu can't unmount it mid-submit.
 */
import { ChevronDown } from 'lucide-react'
import { useRef } from 'react'
import { Link } from 'react-router'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import { createAvatarName } from '#app/utils/avatar-name'
import { cn } from '#app/utils/misc'
import { useOptionalUser } from '#app/utils/user'
import { MENU_PANEL_CLASS } from './HeaderMenus'

export function HeaderProfileMenu({ className }: { className?: string }) {
	const logoutForm = useRef<HTMLFormElement>(null)
	// Mid-logout the root loader has already cleared the user; render nothing
	// rather than throwing via useUser() (same guard SiteHeader uses).
	const user = useOptionalUser()
	if (!user) return null

	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				aria-label="Account menu"
				className={cn(
					'flex h-11 cursor-pointer items-center gap-2 rounded-full px-1.5 transition-colors hover:bg-[#F6F7F5]',
					className,
				)}
			>
				<span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#d9e0ea]">
					<span className="text-xs font-semibold text-[#32302F]">
						{createAvatarName(user.name ?? '')}
					</span>
				</span>
				{/* The name/role block is desktop-only — the top bar is already
				    dense at tablet widths, where the avatar alone identifies it. */}
				<span className="hidden flex-col text-left leading-tight lg:flex">
					<span className="text-sm font-medium text-[#32302F]">
						{user.name}
					</span>
					<span className="text-[12px] text-[#747474] capitalize">
						{user.userType}
					</span>
				</span>
				<ChevronDown className="hidden size-4 text-[#7A7A7A] lg:block" />
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				sideOffset={10}
				className={`${MENU_PANEL_CLASS} w-48 p-1.5`}
			>
				<DropdownMenuItem
					asChild
					className="cursor-pointer rounded-lg px-3 py-2 text-sm text-[#32302F] focus:bg-[#F6F7F5]"
				>
					<Link to="/settings">Settings</Link>
				</DropdownMenuItem>
				<DropdownMenuItem
					className="cursor-pointer rounded-lg px-3 py-2 text-sm text-[#32302F] focus:bg-[#F6F7F5]"
					onSelect={() => logoutForm.current?.requestSubmit()}
				>
					Logout
				</DropdownMenuItem>
			</DropdownMenuContent>
			<form
				ref={logoutForm}
				method="POST"
				action="/logout"
				className="hidden"
			/>
		</DropdownMenu>
	)
}
